/**
 * Витрина каталога услуг для гостя — корневая сетка категорий (epic #220,
 * фаза 5; приведение к канону design-guest 2026-06-02 §6/§15).
 *
 * UX-поток (canon, v4 правка #7 Артёма):
 *   /services (сетка категорий) → /services/{categoryCode} (tile-rows услуг,
 *   CategoryServicesPage) → /requests/new?service={itemId} (RequestNewPage).
 *
 * Паттерны (BINDING):
 *   - загрузка — skeleton, не spinner (design round 2, п.6);
 *   - тон Friendly, «Вы» с заглавной (round 2, п.11);
 *   - мобайл 375 (CSS в styles/global.css).
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CategoryIcon, Skeleton } from '@hsc/ui';
import { isFnbCategoryCode } from '@hsc/types';
import { resolveGuestLocale } from '../../lib/locale.js';
import { useServiceCatalog } from './useCatalog.js';

export function CatalogPage(): JSX.Element {
  const locale = useMemo(() => resolveGuestLocale(), []);
  const { data, isLoading, isError } = useServiceCatalog(locale);
  const navigate = useNavigate();

  // F&B (кухня/бар/minibar/room_service) — это НЕ отдельные типы заявок. Все
  // меню-категории схлопываем в ОДИН пункт «Кухня и бар» (правка Артёма
  // 2026-06-17): он открывает меню с табами бар/кухня и корзиной-оверлеем
  // (/menu), а не список из 30+ меню-категорий в каталоге заявок. Прочие
  // категории (housekeeping/concierge/…) остаются как есть.
  const nonFnb = useMemo(
    () => (data?.categories ?? []).filter((c) => !isFnbCategoryCode(c.code)),
    [data],
  );
  const hasFnb = useMemo(
    () => (data?.categories ?? []).some((c) => isFnbCategoryCode(c.code) && c.items.length > 0),
    [data],
  );

  if (isLoading) {
    return (
      <section aria-busy="true">
        <h1 className="hsc-page__title">Услуги отеля</h1>
        <div className="cat-grid" data-testid="catalog-skeleton">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} height={120} radius={20} />
          ))}
        </div>
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section>
        <h1 className="hsc-page__title">Услуги отеля</h1>
        <Card>
          <p role="alert">Не удалось загрузить каталог услуг. Попробуйте обновить страницу.</p>
        </Card>
      </section>
    );
  }

  return (
    <section data-testid="catalog-page">
      <h1 className="hsc-page__title">Услуги отеля</h1>
      {nonFnb.length === 0 && !hasFnb ? (
        <Card>
          <p>Пока ничего нет — отель ещё наполняет каталог. Загляните чуть позже.</p>
        </Card>
      ) : (
        <div className="cat-grid">
          {/* ОДИН пункт «Кухня и бар» вместо россыпи меню-категорий → /menu. */}
          {hasFnb && (
            <button
              type="button"
              className="cat-card"
              data-testid="catalog-category-fnb"
              onClick={() => navigate('/menu')}
            >
              <span className="cat-card__icon">
                <CategoryIcon code="room_service" size={26} />
              </span>
              <span className="cat-card__name">Меню</span>
              <span className="cat-card__desc">Заказ еды и напитков в номер</span>
              <span className="cat-card__count">Открыть меню</span>
            </button>
          )}
          {nonFnb.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className="cat-card"
              data-testid={`catalog-category-${cat.code}`}
              onClick={() => navigate(`/services/${encodeURIComponent(cat.code)}`)}
            >
              <span className="cat-card__icon">
                <CategoryIcon code={cat.code} size={26} />
              </span>
              <span className="cat-card__name">{cat.name}</span>
              {cat.description !== undefined && cat.description !== '' && (
                <span className="cat-card__desc">{cat.description}</span>
              )}
              <span className="cat-card__count">
                {cat.items.length > 0 ? `Услуг: ${cat.items.length}` : 'Свободная заявка'}
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
